import { admin2 } from './instance'

const baseUrl = (shopId) => `/admin/${shopId}/invoice`
const configUrl = (shopId) => `/${shopId}/invoiceConfig`
const recordUrl = (shopId) => `/${shopId}/invoiceRecord`

// ---------------------------------------------------------------------------
// BDV 載具驗證
// ---------------------------------------------------------------------------

/**
 * 驗證手機條碼是否有效（BDV）
 * featureKey: admin.invoiceCarrier.validate
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} params.carrierNum - 載具號碼
 * @param {string} params.source - 發票來源模組 (appointmentCheckout|course|memberStore|pubApt)
 * @returns {Promise<{valid: boolean, message: string, errorCode?: string}>}
 */
export const ValidateInvoiceCarrier = async ({ shopId, carrierNum, source }) => {
  return await admin2({
    method: 'POST',
    url: `${baseUrl(shopId)}/carrier/validate`,
    data: {
      carrierNum,
      source,
    },
  })
}

// ---------------------------------------------------------------------------
// §4.3.10-12 InvoiceConfig
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} InvoiceConfig
 * @property {string} id - 發票設定 ID
 * @property {boolean} enabled - 是否啟用
 * @property {string} platform - 發票平台
 * @property {Object} invoiceApiConfig - 發票 API 設定
 * @property {string} invoiceApiConfig.mode - 發票 API 模式
 * @property {string} invoiceApiConfig.hashIV - 發票 API Hash IV
 * @property {string} invoiceApiConfig.hashKey - 發票 API Hash Key
 * @property {string} invoiceApiConfig.merchantID - 發票 API 商家 ID
 * @property {Array<string>} allowedTypes - 允許的發票類型
 * @property {Object} sellerInfo - 賣方資訊
 * @property {string} sellerInfo.name - 賣方名稱
 * @property {string} sellerInfo.address - 賣方地址
 * @property {string} sellerInfo.phone - 賣方電話
 * @property {string} sellerInfo.email - 賣方 Email
 */

/**
 * @typedef {Object} PrinterConfig
 * @property {string} id - 發票機 ID
 * @property {boolean} enabled - 是否啟用
 * @property {string} model - 發票機模型
 * @property {Object} connectionInfo - 連線資訊
 * @property {string} connectionInfo.id - 裝置識別碼
 * @property {string} connectionInfo.password - 裝置密碼
 * @property {string} lastTestPrintStatus - 最後測試列印狀態
 * @property {string} lastTestPrintAt - 最後測試列印時間
 */

/**
 * 取得電子發票設定
 * GET /admin/{shopId}/invoiceConfig
 * @param {string} shopId - 店家 ID
 * @returns {import('@/api/instance').Api2Response<{
 *   invoice: InvoiceConfig,
 *   printer: PrinterConfig,
 * }>}
 * @example
 * {
    "invoice": {
        "id": "6e3adaf5-cd24-42f6-969b-9242f4564871",
        "enabled": false,
        "platform": "ezpay",
        "invoiceApiConfig": {
            "mode": "dev",
            "hashIV": "xxx",
            "hashKey": "xx",
            "merchantID": "12345678"
        },
        "sellerInfo": {
            "name": "xxx",
            "identifier": "12345678"
        }
    },
    "printer": {
        "id": "3f05febb-9d05-45e3-bb66-c7f2c1c8049c",
        "enabled": false,
        "model": "epson_tm_m30iii",
        "connectionInfo": {
            "id": "PRN-Irm8XsVig5",
            "password": "12341234"
        },
        "lastTestPrintStatus": "success",
        "lastTestPrintAt": "2026-06-24T08:57:34.910Z"
    }
}
 */
export const GetInvoiceConfig = async ({ shopId }) => {
  return await admin2({
    method: 'GET',
    url: configUrl(shopId),
  })
}

/**
 * 更新電子發票設定
 * PUT /admin/{shopId}/invoiceConfig
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {Omit<InvoiceConfig, 'id'>} params.invoice - 發票設定
 * @param {Omit<PrinterConfig, 'id'>} params.printer - 發票機設定
 * @returns {import('@/api/instance').Api2Response<{
 *   invoice: InvoiceConfig,
 *   printer: PrinterConfig,
 * }>}
 * @example
 * {
    "invoice": {
        "id": "6e3adaf5-cd24-42f6-969b-9242f4564871",
        "enabled": false,
        "platform": "ezpay",
        "invoiceApiConfig": {
            "mode": "dev",
            "hashIV": "xxx",
            "hashKey": "xx",
            "merchantID": "12345678"
        },
        "sellerInfo": {
            "name": "xxx",
            "identifier": "12345678"
        }
    },
    "printer": {
        "id": "3f05febb-9d05-45e3-bb66-c7f2c1c8049c",
        "enabled": false,
        "model": "epson_tm_m30iii",
        "connectionInfo": {
            "id": "PRN-Irm8XsVig5",
            "password": "12341234"
        },
        "lastTestPrintStatus": "success",
        "lastTestPrintAt": "2026-06-24T08:57:34.910Z"
    }
}
 */
export const UpdateInvoiceConfig = async ({ shopId, invoice, printer }) => {
  return await admin2({
    method: 'PUT',
    url: configUrl(shopId),
    data: { invoice, printer },
  })
}

/**
 * 測試列印發票
 * POST /admin/{shopId}/invoiceConfig/testPrint
 * @param {string} shopId - 店家 ID
 */
export const TestPrintInvoice = async ({ shopId }) => {
  return await admin2({
    method: 'POST',
    url: `${configUrl(shopId)}/testPrint`,
  })
}

/**
 * 測試電子發票串接資訊是否正確（唯讀，不會開立任何發票）
 * POST /admin/{shopId}/invoiceConfig/verify
 * featureKey: admin.invoiceConfig.verify
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {Object} [params.invoiceApiConfig] - 欲測試的串接設定 { merchantID, hashKey, hashIV }；
 *   帶入表單當前（尚未存檔）的值可做「存檔前先測」，未帶則測試店家已存設定
 * @param {string} [params.platform] - 串接平台，預設 ezpay
 * @returns {import('@/api/instance').Api2Response<{valid: boolean, message: string, errorCode?: string}>}
 */
export const VerifyInvoiceConfig = async ({ shopId, invoiceApiConfig, platform }) => {
  return await admin2({
    method: 'POST',
    url: `${configUrl(shopId)}/verify`,
    data: { invoiceApiConfig, platform },
  })
}

// ---------------------------------------------------------------------------
// §4.3.1-8 InvoiceRecord
// ---------------------------------------------------------------------------

/**
 * 開立發票
 * POST /admin/{shopId}/invoiceRecord/issue
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {Object} params.invoiceData - 發票內容 { total, buyer, items }
 */
export const IssueInvoiceRecord = async ({ shopId, invoiceData }) => {
  return await admin2({
    method: 'POST',
    url: `${recordUrl(shopId)}/issue`,
    data: { invoiceData },
  })
}

/**
 * 取得單一發票紀錄
 * GET /admin/{shopId}/invoiceRecord/{invoiceRecordId}
 * @param {string} shopId - 店家 ID
 * @param {string} id - 發票紀錄 ID
 */
export const GetInvoiceRecord = async ({ shopId, id }) => {
  return await admin2({
    method: 'GET',
    url: `${recordUrl(shopId)}/${id}`,
  })
}

/**
 * 取得發票紀錄列表
 * GET /admin/{shopId}/invoiceRecord
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {number} [params.start] - 起始 offset
 * @param {number} [params.limit] - 筆數
 * @param {string} [params.invoiceNo] - 發票號碼（模糊）
 * @param {string} [params.sourceNo] - 訂單編號
 * @param {string} [params.buyerEmail] - 買方 Email（模糊）
 * @param {string} [params.buyerKeyword] - 手機條碼或統編
 * @param {string} [params.status] - 發票狀態
 * @param {string} [params.printStatus] - 列印狀態
 * @param {string} [params.sourceType] - 來源渠道
 * @param {string} [params.issuedAtStart] - 開立起日
 * @param {string} [params.issuedAtEnd] - 開立迄日
 */
export const GetInvoiceRecords = async ({
  shopId, start, limit, invoiceNo, sourceNo, buyerEmail,
  buyerKeyword, status, printStatus, sourceType, issuedAtStart, issuedAtEnd,
}) => {
  return await admin2({
    method: 'GET',
    url: recordUrl(shopId),
    params: { start, limit, invoiceNo, sourceNo, buyerEmail, buyerKeyword, status, printStatus, sourceType, issuedAtStart, issuedAtEnd },
  })
}

/**
 * 取得發票紀錄數量
 * GET /admin/{shopId}/invoiceRecord/count
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} [params.invoiceNo] - 發票號碼（模糊）
 * @param {string} [params.sourceNo] - 訂單編號
 * @param {string} [params.buyerEmail] - 買方 Email（模糊）
 * @param {string} [params.buyerKeyword] - 手機條碼或統編
 * @param {string} [params.status] - 發票狀態
 * @param {string} [params.printStatus] - 列印狀態
 * @param {string} [params.sourceType] - 來源渠道
 * @param {string} [params.issuedAtStart] - 開立起日
 * @param {string} [params.issuedAtEnd] - 開立迄日
 */
export const GetInvoiceRecordCount = async ({
  shopId, invoiceNo, sourceNo, buyerEmail, buyerKeyword,
  status, printStatus, sourceType, issuedAtStart, issuedAtEnd,
}) => {
  return await admin2({
    method: 'GET',
    url: `${recordUrl(shopId)}/count`,
    params: { invoiceNo, sourceNo, buyerEmail, buyerKeyword, status, printStatus, sourceType, issuedAtStart, issuedAtEnd },
  })
}

/**
 * 作廢發票
 * POST /admin/{shopId}/invoiceRecord/{invoiceRecordId}/invalid
 * @param {string} shopId - 店家 ID
 * @param {string} id - 發票紀錄 ID
 * @param {string} [invalidReason] - 作廢原因
 */
export const InvalidInvoiceRecord = async ({ shopId, id, invalidReason }) => {
  return await admin2({
    method: 'POST',
    url: `${recordUrl(shopId)}/${id}/invalid`,
    data: { invalidReason },
  })
}

/**
 * 折讓發票（部分或全額）
 * POST /admin/{shopId}/invoiceRecord/{invoiceRecordId}/allowance
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} params.id - 發票紀錄 ID
 * @param {string} [params.buyerEmail] - 折讓單 Email
 * @param {Array} params.items - 折讓品項 [{ invoiceItemId, name, quantity, amount, taxAmount }]
 */
export const AllowanceInvoiceRecord = async ({ shopId, id, buyerEmail, items }) => {
  return await admin2({
    method: 'POST',
    url: `${recordUrl(shopId)}/${id}/allowance`,
    data: { buyerEmail, items },
  })
}

/**
 * 補印發票
 * POST /admin/{shopId}/invoiceRecord/{invoiceRecordId}/reprint
 * @param {string} shopId - 店家 ID
 * @param {string} id - 發票紀錄 ID
 */
export const ReprintInvoiceRecord = async ({ shopId, id }) => {
  return await admin2({
    method: 'POST',
    url: `${recordUrl(shopId)}/${id}/reprint`,
  })
}

/**
 * 重開發票
 * POST /admin/{shopId}/invoiceRecord/{invoiceRecordId}/reissue
 * @param {string} shopId - 店家 ID
 * @param {string} id - 發票紀錄 ID
 */
export const ReissueInvoiceRecord = async ({ shopId, id }) => {
  return await admin2({
    method: 'POST',
    url: `${recordUrl(shopId)}/${id}/reissue`,
  })
}

/**
 * 跨渠道統一發票詳情
 * GET /admin/{shopId}/invoiceRecord/detail/{bridgeId}?sourceType=...
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} params.bridgeId - 各來源橋接表 PK
 * @param {string} params.sourceType - 來源類型（manual | salesRecord | course | ...）
 */
export const FindInvoiceDetail = async ({ shopId, bridgeId, sourceType }) => {
  return await admin2({
    method: 'GET',
    url: `${recordUrl(shopId)}/detail/${bridgeId}`,
    params: { sourceType },
  })
}

// ---------------------------------------------------------------------------
// §4.3.13-14 SalesInvoiceConfig
// ---------------------------------------------------------------------------

const salesConfigUrl = (shopId) => `/${shopId}/salesInvoiceConfig`

/**
 * 取得銷售發票設定
 * GET /admin/{shopId}/salesInvoiceConfig
 * @param {string} shopId - 店家 ID
 */
export const GetSalesInvoiceConfig = async ({ shopId }) => {
  return await admin2({
    method: 'GET',
    url: salesConfigUrl(shopId),
  })
}

/**
 * 更新銷售發票設定
 * PUT /admin/{shopId}/salesInvoiceConfig
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} params.defaultInvoiceOption - POS 出帳出帳彈窗預設選項 (none | issue)
 * @param {boolean} params.posInvoiceEnabled - 是否在 POS 出帳流程開放開票
 */
export const UpdateSalesInvoiceConfig = async ({ shopId, defaultInvoiceOption, posInvoiceEnabled }) => {
  return await admin2({
    method: 'PUT',
    url: salesConfigUrl(shopId),
    data: { defaultInvoiceOption, posInvoiceEnabled },
  })
}
